import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import React from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { setHours, setMinutes } from 'date-fns';
import { FormInputProps } from '../../componets/form/formInterfaces';
import { IconComponent, icons } from '../../componets/icons';
import { IFormInputPropsFilter } from '../../componets/interfaces/formInputPropsFilter';
import { IFormSettinsProps } from '../../componets/interfaces/formProps';
import { ImperativeHandleAccessedByParentListComponent } from '../../componets/interfaces/listProps';
import ListComponet from '../../componets/list';
import { rowsPerPageOptions } from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
    formatDateWithoutHours,
    momentZoneToDateAddDays,
    momentZoneToUnix,
} from '../../util/dateUtil';
import './styles.scss';
import { setTypeUserInQuery } from '../../util/configTypeUser';

const defaultValues = {
    customer: {
        id: '',
        name: '',
    },
    user: [],
    situation: [],
    dateInitial: momentZoneToDateAddDays({ days: -90, startOfType: 'day' }),
    dateFinal: momentZoneToDateAddDays({ days: 0, endOfType: 'day' }),
    processNumber: '',
    code: '',
};

const AdditionalHonorariumList: React.FC<IFormSettinsProps> = props => {
    const { settings } = props;

    const refForm =
        React.useRef<ImperativeHandleAccessedByParentListComponent>(null);

    const { user } = useAuth();
    const { entity } = settings;

    const QUERY_DEFAULT = `page=1&limit=${
        rowsPerPageOptions[0]
    }&dateInitial=${momentZoneToUnix(
        defaultValues.dateInitial,
    )}&dateFinal=${momentZoneToUnix(defaultValues.dateFinal)}&user.id.array=${
        user.sub
    }&additionalHonorarium.origin.notarray=2,3`;

    const inputsFilter: IFormInputPropsFilter[] = [
        {
            typeInput: 'text',
            name: `${entity}.code.equal`,
            control: null,
            label: 'Código',
            setValue: null,
            size: 'small',
            variant: 'standard',
            handleOnKeyPress: (data: any, name?: string) => {
                if (data.charCode === 13 && name) {
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        data.target.value,
                        name,
                    );
                }
            },
        },
        {
            typeInput: 'text',
            name: `additionalHonorarium.processNumber.ilike`,
            control: null,
            label: 'Número processo',
            setValue: null,
            size: 'small',
            variant: 'standard',
            handleOnKeyPress: (data: any, name?: string) => {
                if (data.charCode === 13 && name) {
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        data.target.value,
                        name,
                    );
                }
            },
        },
        {
            typeInput: 'autocomplete',
            name: 'customer.id',
            variant: 'standard',
            control: null,
            label: 'Cliente',
            loadingAutocomplete: false,
            setValue: null,
            handleChange: (event: any, option: any, name: string) => {
                if (event.type === 'click' || event.type === 'keydown') {
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        option?.id,
                        name,
                    );
                }
            },
            activeDebounce: true,
            query: (value: string) => `customer?name=${value}`,
        },
        {
            typeInput: 'multiple',
            name: 'user.id.array',
            variant: 'standard',
            control: null,
            label: 'Advogado',
            handleChange: (event: any, option: any[]) => {
                if (event.type === 'click' || event.type === 'keydown') {
                    const data = option?.map(o => o.id);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        data.length > 0 ? data : null,
                        'user.id.array',
                    );
                }
            },
            setValue: null,
            entityPath: 'users',
            keyQuery: 'user.id',
            activeDebounce: true,
            query: (value: string) => `users?${setTypeUserInQuery(user.typeUser)}&limit=${value}`,
            DAODataList: (value: any) => {
                return {
                    id: value.id,
                    description: value.name,
                };
            },
        },
    ];

    const mainInputsFilter = React.useMemo<FormInputProps[]>(
        () => [
            {
                typeInput: 'dateHour',
                name: 'dateInitial',
                control: null,
                label: 'Date de início',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 0);
                    newTime = setMinutes(newTime, 1);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateInitial',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
            {
                typeInput: 'dateHour',
                name: 'dateFinal',
                control: null,
                label: 'Date de fim',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 23);
                    newTime = setMinutes(newTime, 59);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateFinal',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
        ],
        [],
    );

    const columnsTable = React.useMemo<ColumnDef<Object>[]>(
        () => [
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Código
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.code}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Origem
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.itemHonoraries
                                ?.map((o: any) => o.originType.description)
                                .join(',')}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Número processo
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.processNumber}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'customer',
                cell: (cell: any) => {
                    return (
                        <TableCell
                            align="left"
                            onClick={() =>
                                refForm?.current?.handleClick(
                                    cell.row.original.id,
                                )
                            }>
                            {cell.row.original.customer.name}
                        </TableCell>
                    );
                },
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Cliente
                    </TableCell>
                ),
            },
            {
                id: 'user',
                header: (info: any) => {
                    return (
                        <TableCell align={'left'} padding={'normal'}>
                            Advogado
                        </TableCell>
                    );
                },
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.user?.name}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'dateSpend',
                cell: (cell: any) => (
                    <TableCell
                        align="left"
                        onClick={() =>
                            refForm?.current?.handleClick(cell.row.original.id)
                        }>
                        {formatDateWithoutHours(cell?.getValue())}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Data
                    </TableCell>
                ),
            },
            {
                id: 'select',
                header: ({ table }: any) => <TableCell align="center" />,
                cell: ({ row }: any) => (
                    <TableCell align="right">
                        <IconButton
                            onClick={() =>
                                refForm?.current?.handleEdit(row.original.id)
                            }>
                            <IconComponent icon={icons.ModeEdit} />
                        </IconButton>
                        <IconButton
                            onClick={() => {
                                refForm?.current?.setIdDelete(row.original.id);
                                refForm?.current?.setOpenModalDelete(true);
                            }}>
                            <IconComponent icon={icons.Delete} />
                        </IconButton>
                    </TableCell>
                ),
            },
        ],
        [],
    );

    return (
        <ListComponet
            settings={settings}
            defaultValues={defaultValues}
            columnsTable={columnsTable}
            inputsFilter={inputsFilter}
            mainInputsFilter={mainInputsFilter}
            QUERY_DEFAULT={QUERY_DEFAULT}
            ref={refForm}
        />
    );
    // return (
    //     <div className="principal-container-additional-honorarium">
    //         <AlertDialog
    //             handleConfirmation={handleConfirmeDelete}
    //             open={openModalDelete}
    //         />
    //         <Box className={toggleSearch ? 'container-box' : ''}>
    //             <Paper sx={{ width: '100%', mb: 2 }}>
    //                 <div className="header-list">
    //                     <div className="header-list-title">
    //                         <Typography
    //                             sx={{
    //                                 flex: '1 1 100%',
    //                             }}
    //                             variant="h6"
    //                             id="tableTitle"
    //                             component="div">
    //                             {settings.tittle}
    //                         </Typography>
    //                     </div>
    //                     <div className="header-list-search">
    //                         <Stack
    //                             spacing={1}
    //                             direction="row"
    //                             width={'100%'}
    //                             justifyContent="space-between"
    //                             alignItems="flex-end">
    //                             <Grid container spacing={2}>
    //                                 <Grid item md={6} xs={12}>
    //                                     <FormInputDateWithoutHour
    //                                         name={inputsFilter[0].name}
    //                                         control={inputsFilter[0].control}
    //                                         rules={null}
    //                                         label={inputsFilter[0].label}
    //                                         setValue={inputsFilter[0].setValue}
    //                                         fullWidth={
    //                                             inputsFilter[0].fullWidth ||
    //                                             false
    //                                         }
    //                                         setError={inputsFilter[0].setError}
    //                                         clearErrors={
    //                                             inputsFilter[0].clearErrors
    //                                         }
    //                                         handleChange={
    //                                             inputsFilter[0].handleChange
    //                                         }
    //                                     />
    //                                 </Grid>
    //                                 <Grid item md={6} xs={12}>
    //                                     <FormInputDateWithoutHour
    //                                         name={inputsFilter[1].name}
    //                                         control={inputsFilter[1].control}
    //                                         rules={null}
    //                                         label={inputsFilter[1].label}
    //                                         setValue={inputsFilter[1].setValue}
    //                                         fullWidth={
    //                                             inputsFilter[1].fullWidth ||
    //                                             false
    //                                         }
    //                                         setError={inputsFilter[1].setError}
    //                                         clearErrors={
    //                                             inputsFilter[1].clearErrors
    //                                         }
    //                                         handleChange={
    //                                             inputsFilter[1].handleChange
    //                                         }
    //                                     />
    //                                 </Grid>
    //                             </Grid>
    //                             {/* <Tooltip
    //                                 title="Limpar filtro de datas"
    //                                 onClick={() => handleClearDates()}>
    //                                 <IconButton>
    //                                     <IconComponent icon={icons.ClearAll} />
    //                                 </IconButton>
    //                             </Tooltip> */}
    //                             <Tooltip
    //                                 title="Opções de filtros"
    //                                 onClick={() => setToggleSearch(true)}>
    //                                 <IconButton data-qa="filter-options">
    //                                     <FilterListIcon />
    //                                 </IconButton>
    //                             </Tooltip>
    //                         </Stack>
    //                     </div>
    //                 </div>
    //                 <Typography
    //                     sx={{
    //                         pl: { xs: 1, sm: 2 },
    //                         pr: { xs: 1, sm: 1 },
    //                     }}>
    //                     <FormButton
    //                         label={'Novo'}
    //                         typeButton="addRegister"
    //                         onClick={() => history.push(settings.pathRegister)}
    //                     />
    //                 </Typography>
    //                 <TableGeneric
    //                     data={rows}
    //                     columns={columnsTable}
    //                     minWidth={650}
    //                     sxTableContainer={{
    //                         height: width <= 800 ? 400 : 700,
    //                         '&::-webkit-scrollbar': {
    //                             width: 8,
    //                         },
    //                         '&::-webkit-scrollbar-track': {
    //                             backgroundColor: '#f1f1f1',
    //                             borderRadius: 2,
    //                         },
    //                         '&::-webkit-scrollbar-thumb': {
    //                             backgroundColor: '#c1c1c1',
    //                             borderRadius: 2,
    //                         },
    //                     }}
    //                 />
    //                 <TablePagination
    //                     rowsPerPageOptions={rowsPerPageOptions}
    //                     component="div"
    //                     count={total}
    //                     labelRowsPerPage={'Linhas por página'}
    //                     labelDisplayedRows={defaultLabelDisplayedRows}
    //                     rowsPerPage={rowsPerPage}
    //                     page={total > 0 ? page : 0}
    //                     onPageChange={(event: unknown, newPage: number) =>
    //                         handleChangePage(
    //                             event,
    //                             newPage,
    //                             setPage,
    //                             history,
    //                             loadRows,
    //                         )
    //                     }
    //                     onRowsPerPageChange={(
    //                         event: React.ChangeEvent<HTMLInputElement>,
    //                     ) =>
    //                         handleChangeRowsPerPage(
    //                             event,
    //                             setRowsPerPage,
    //                             setPage,
    //                             history,
    //                             loadRows,
    //                         )
    //                     }
    //                 />
    //             </Paper>
    //         </Box>

    //         <FilterSearchComponent
    //             inputs={inputsFilter}
    //             setToggleSearch={setToggleSearch}
    //             toggleSearch={toggleSearch}
    //             setRowsPerPage={setRowsPerPage}
    //             setPage={setPage}
    //             rowsPerPage={rowsPerPage}
    //             page={page}
    //             implementationLoadRows={loadRows}
    //             updateRows={updateRows}
    //             setUpdateRows={setUpdateRows}
    //             queryDefault={QUERY_DEFAULT}
    //         />
    //     </div>
    // );
};

export default AdditionalHonorariumList;
